import { useMemo } from 'react'
import { X, Plane } from '@/components/icons'
import { Button } from './ui/button'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from './ui/drawer'
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar'
import { MenuItem } from './ui/menu-item'
import { cn, getInitials, getDutyStatusColor } from '@/lib/utils'
import { crewRoleLabels, DutyStatus } from '@@api/shared'
import { useTripLegsByCrewAndDateRange, useAircraftMaintenance } from '@/lib/hooks'
import { MaintenanceDayList } from './MaintenanceDayList'
import dayjs, { Dayjs } from 'dayjs'

interface CrewInfo {
  id: string
  name: string
  firstName?: string | null
  lastName?: string | null
  imageUrl?: string | null
  avatarColor?: string | null
  crewRole?: string
}

export interface RosterEntryWithCrew {
  id: string
  date: Dayjs
  crewId: string
  dutyStatus: DutyStatus
  crew?: CrewInfo
}

export interface AdminCrewRosterCalendarDrawerProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  date: Date | null
  entries: RosterEntryWithCrew[]
  onEntryClick?: (entry: RosterEntryWithCrew) => void
  /** Aircraft filter (single id narrows maintenance to that aircraft) */
  aircraftIds?: string[]
}

export function AdminCrewRosterCalendarDrawer({
  isOpen,
  onOpenChange,
  date,
  entries,
  onEntryClick,
  aircraftIds,
}: AdminCrewRosterCalendarDrawerProps) {
  // Aircraft in maintenance for this day, scoped to the current aircraft filter
  const { getMaintenanceForDay } = useAircraftMaintenance(aircraftIds)
  const maintenanceAircraft = date ? getMaintenanceForDay(dayjs(date)) : []
  // Get unique crew members from entries
  const uniqueCrewEntries = useMemo(() => {
    return entries.reduce((acc, entry) => {
      if (entry.crew && !acc.find((e) => e.crewId === entry.crewId)) {
        acc.push(entry)
      }
      return acc
    }, [] as RosterEntryWithCrew[])
  }, [entries])

  const crewIds = useMemo(() => uniqueCrewEntries.map((e) => e.crewId), [uniqueCrewEntries])

  // Normalize the calendar date to UTC so "April 12" queries April 12 00:00Z–23:59Z
  // (not shifted by the browser's local timezone)
  const utcDate = date
    ? new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
    : undefined

  // Fetch flight assignments for the crew on this date
  const { data: tripLegs = [] } = useTripLegsByCrewAndDateRange({
    crewIds,
    startDate: utcDate,
    endDate: utcDate,
  })

  // Build a set of crewIds that have flights on this date
  const crewIdsWithFlights = useMemo(() => {
    const ids = new Set<string>()
    for (const leg of tripLegs as any[]) {
      for (const crew of leg.crew ?? []) {
        if (crewIds.includes(crew.id)) ids.add(crew.id)
      }
    }
    return ids
  }, [tripLegs, crewIds])

  return (
    <Drawer open={isOpen} onOpenChange={onOpenChange}>
      <DrawerContent className="bg-surface-L0 max-h-[85dvh] flex flex-col">
        <DrawerHeader className="flex items-center justify-between p-4">
          <DrawerTitle className="text-lg font-semibold text-content-primary-bold">
            {date ? dayjs(date).format('MMMM D') : 'Scheduled Crew'}
          </DrawerTitle>
          <DrawerClose asChild>
            <Button variant="secondary" size="icon" roundness="round" className="size-8">
              <X className="w-4 h-4" />
            </Button>
          </DrawerClose>
        </DrawerHeader>

        {/* Aircraft in maintenance for this day */}
        {maintenanceAircraft.length > 0 && (
          <div className="px-4 pb-4">
            <MaintenanceDayList aircraft={maintenanceAircraft} />
          </div>
        )}

        {/* Crew list */}
        <div className="flex flex-col gap-1 px-4 pb-4 max-h-[60vh] overflow-y-auto">
          {uniqueCrewEntries.length === 0 ? (
            <p className="text-sm text-content-primary-subtle py-4 text-center">
              No crew scheduled for this date
            </p>
          ) : (
            uniqueCrewEntries.map((entry) => {
              const crew = entry.crew
              if (!crew) return null

              const initials = getInitials(crew.firstName, crew.lastName)
              const roleLabel = crew.crewRole ? (crewRoleLabels[crew.crewRole] || crew.crewRole) : ''
              const hasFlight = crewIdsWithFlights.has(entry.crewId)

              return (
                <MenuItem
                  key={entry.id}
                  size="large"
                  onClick={() => onEntryClick?.(entry)}
                  leftIcon={
                    <Avatar className="size-8">
                      {crew.imageUrl ? <AvatarImage src={crew.imageUrl} /> : null}
                      <AvatarFallback
                        className="text-background text-xs"
                        style={crew.avatarColor ? { backgroundColor: crew.avatarColor } : undefined}
                      >
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                  }
                  secondaryText={roleLabel || undefined}
                  rightIcon={
                    <div className="flex items-center gap-2">
                      {hasFlight && (
                        <Plane className="size-4 text-content-primary-subtle" />
                      )}
                      <div
                        className={cn(
                          'size-3 rounded-full shrink-0',
                          getDutyStatusColor(entry.dutyStatus)
                        )}
                      />
                    </div>
                  }
                >
                  {crew.name}
                </MenuItem>
              )
            })
          )}
        </div>
      </DrawerContent>
    </Drawer>
  )
}
